// ============================================================================
// Types for Email Automation Pipeline Results
// ============================================================================

// Import core types from backend
import type {
  DiagnosticEvent as BackendDiagnosticEvent,
  StepDiagnostics as BackendStepDiagnostics,
  ActionDetailApi,
  DiagnosticCounts,
  EmailAutomationSection,
  ExtractedProperty,
  GptRequestInfo,
} from "@services/emailAutomationPipeline/types";

// Re-export backend types that can be used directly
export type { DiagnosticCounts, EmailAutomationSection, ExtractedProperty, GptRequestInfo };

// Frontend versions with string timestamps (JSON serialization converts Date to string)
export interface DiagnosticEvent extends Omit<BackendDiagnosticEvent, "timestamp"> {
  timestamp: string; // ISO string from JSON
}

export interface DiagnosticEvents {
  events: DiagnosticEvent[];
}

export interface StepDiagnostics extends Omit<BackendStepDiagnostics, "diagnosticEvents" | "startedAt" | "completedAt"> {
  diagnosticEvents: DiagnosticEvents;
  startedAt: string;
  completedAt?: string;
}

export type { ActionDetailApi };

export interface InputItem {
  emailId?: string;
  shipmentId?: string;
  reminderId?: string;
  reminderType?: string;
  cutOffDate?: string;
  [key: string]: unknown;
}

// Lightweight result for table rendering (stripped data from /results/get)
export interface PipelineResultLight {
  emailId: string;
  input?: InputItem;
  emailSubject?: string;
  inbox?: string; // The inbox where the email came from (sharedInbox (user) or just user)
  categorizationStep?: {
    categories: string[];
  };
  propertyExtractionStep?: {
    properties: ExtractedProperty[];
    totalCount: number;
  };
  apiFetchStep?: {
    dataKeys: string[];
  };
  presentationStep?: {
    presentationPreview: string;
  };
  generateActionsStep?: {
    actionTypes: string[];
  };
  evalStep?: {
    output: Record<string, string>;
  };
  diagnosticCounts?: DiagnosticCounts;
  gptRequestCount: number;
  notes?: string;
}

// Full result with all details (from /results/get-automation-result)
export interface PipelineResultFull {
  emailId: string;
  emailSubject?: string;
  categorizationStep?: {
    categories: string[];
    exchangeType: string;
    oneLineSummary: string;
    context: string;
    diagnostics?: StepDiagnostics;
  };
  propertyExtractionStep?: {
    properties: ExtractedProperty[];
    diagnostics?: StepDiagnostics;
  };
  apiFetchStep?: {
    data: Record<string, unknown>;
    diagnostics?: StepDiagnostics;
  };
  presentationStep?: {
    presentation: string;
    sections?: EmailAutomationSection[];
    diagnostics?: StepDiagnostics;
  };
  generateActionsStep?: {
    actions: Array<{ actionType: string; data: Record<string, unknown> }>;
    actionIds?: string[];
    resolvedActions?: Array<{ actionType: string; data: Record<string, unknown> }>;
    diagnostics?: StepDiagnostics;
  };
  evalStep?: {
    output: Record<string, string>;
    diagnostics?: StepDiagnostics;
  };
  diagnosticEvents?: DiagnosticEvents;
  gptRequests: GptRequestInfo[];
  notes?: string;
}

export interface SavedResult {
  id: string;
  configId: string;
  configSnapshot: any;
  name: string | null;
  testSetId: string | null;
  testSetName: string | null;
  inputs?: InputItem[];
  results: PipelineResultLight[];
  stepsRun: string[];
  runAt: string;
  workflowId: string | null;
  workflowStatus: string;
  processedCount: number;
  totalCount: number;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TestSet {
  id: string;
  name: string;
  emailCount: number;
  shipmentCount: number;
  reminderCount: number;
  inputCount: number;
}

/**
 * Format a test set label showing input breakdown (only non-zero counts)
 */
export function formatTestSetLabel(ts: TestSet): string {
  const parts: string[] = [];
  if (ts.emailCount > 0) parts.push(`${ts.emailCount} emails`);
  if (ts.shipmentCount > 0) parts.push(`${ts.shipmentCount} shipments`);
  if (ts.reminderCount > 0) parts.push(`${ts.reminderCount} reminders`);
  const breakdown = parts.length > 0 ? `: ${parts.join(", ")}` : "";
  return `${ts.name} (${ts.inputCount} inputs${breakdown})`;
}

// Re-export from centralized constants for backward compatibility
export type { PipelineStep, StepConfig } from "../constants";
export { ALL_STEPS, PIPELINE_STEPS, getStepLabel } from "../constants";

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get pipeline-level diagnostic counts (for Status column summary)
 */
export function getPipelineDiagnosticCounts(
  result: PipelineResultLight
): { infos: number; warns: number; errors: number } {
  return {
    infos: result.diagnosticCounts?.infos || 0,
    warns: result.diagnosticCounts?.warns || 0,
    errors: result.diagnosticCounts?.errors || 0,
  };
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString();
}

/**
 * Get counts of events by level from DiagnosticEvents
 */
export function getEventsCount(
  events?: DiagnosticEvents
): { errors: number; warns: number; infos: number } {
  if (!events?.events) return { errors: 0, warns: 0, infos: 0 };
  return {
    errors: events.events.filter((e) => e.level === "error").length,
    warns: events.events.filter((e) => e.level === "warn").length,
    infos: events.events.filter((e) => e.level === "info").length,
  };
}
